import React, { useState, useEffect, useCallback } from 'react';
import { inductionApi, documentApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Download, FileText, Upload } from 'lucide-react';

/**
 * Staff Induction — one question, tied to the joining date.
 *
 * Yes requires the induction document. That is not a UI nicety: the answer is
 * worth 4 marks on the HR executive's Staff Induction KPI, so it has to rest on
 * something a reviewer can open months later. The backend refuses a Yes with no
 * document on file, so the rule holds even if this screen is bypassed.
 */
export default function InductionTab({ employeeId, canEdit }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remark, setRemark] = useState('');
  const [when, setWhen] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await inductionApi.get(employeeId);
      setData(r.data);
      setRemark(r.data.remark || '');
      setWhen(r.data.induction_date || r.data.doj || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the induction record');
    }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    setBusy(true);
    try {
      await documentApi.upload(employeeId, inductionApi.DOC_TYPE, file);
      toast.success('Induction document uploaded');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const save = async (done) => {
    setBusy(true);
    try {
      const r = await inductionApi.save(employeeId, {
        done, induction_date: done ? (when || null) : null, remark,
      });
      setData(r.data);
      toast.success(done ? 'Induction recorded — 4 marks' : 'Recorded as not done — 0 marks');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="bg-white border border-[#E6E9F0] rounded-xl p-6 text-sm text-[#98A1B2]">Loading…</div>;
  }

  const hasDoc = !!data.document;
  const mark = (v) => (
    <span className="inline-block min-w-[32px] text-center rounded-md px-2 py-0.5 font-bold"
      style={{ background: v > 0 ? '#E8EFFD' : '#FDE9EF', color: v > 0 ? '#2563EB' : '#C2151F' }}>{v}</span>
  );

  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-6 space-y-5" data-testid="induction-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Staff Induction</h2>
          <p className="text-sm text-[#5A6377]">
            Joining date <b className="text-[#11182B]">{data.doj || 'not recorded'}</b>
            {!data.doj && <span className="text-[#C2151F]"> — set the joining date first</span>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-[#98A1B2]">Marks</div>
          {mark(data.marks)}
        </div>
      </div>

      <div className="border border-[#E6E9F0] rounded-lg p-4">
        <p className="text-sm font-medium text-[#11182B] mb-3">Has induction been done?</p>
        <div className="flex items-center gap-3">
          <button type="button" disabled={!canEdit || busy} onClick={() => save(true)}
            data-testid="induction-yes"
            className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              data.done ? 'bg-[#E8EFFD] border-[#2563EB] text-[#172554]'
                        : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:border-[#2563EB]'}
              ${(!canEdit || busy) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            Yes {data.done && '✓'}
          </button>
          <button type="button" disabled={!canEdit || busy} onClick={() => save(false)}
            data-testid="induction-no"
            className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              data.recorded_at && !data.done ? 'bg-[#FDE9EF] border-[#C2151F] text-[#8A1119]'
                        : 'bg-white border-[#E6E9F0] text-[#5A6377] hover:border-[#C2151F]'}
              ${(!canEdit || busy) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            No {data.recorded_at && !data.done && '✓'}
          </button>
          <span className="text-[12px] text-[#98A1B2]">Yes = 4 marks · No = 0 marks</span>
        </div>

        {/* The document is asked for ONLY on Yes — a "No" has nothing to evidence. */}
        {data.done || hasDoc ? (
          <div className="mt-4 pt-4 border-t border-[#F0F2F6]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {hasDoc ? <CheckCircle2 className="w-5 h-5 text-[#15A36E]" />
                        : <Circle className="w-5 h-5 text-[#E9A23B]" />}
                <div>
                  <p className="text-sm font-medium text-[#11182B]">Induction document</p>
                  {hasDoc ? (
                    <p className="text-[11px] text-[#98A1B2]">
                      {data.document.filename} · {new Date(data.document.uploaded_at).toLocaleDateString()}
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#E9A23B]">Required before this can be marked Yes</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasDoc && (
                  <a href={documentApi.download(data.document.id)} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm" className="text-[#2563EB]" data-testid="induction-doc-download">
                      <Download className="w-4 h-4" />
                    </Button>
                  </a>
                )}
                {canEdit && (
                  <label data-testid="induction-upload"
                    className="text-xs text-[#2563EB] cursor-pointer px-3 py-1.5 border border-[#E6E9F0] rounded-md hover:bg-[#EFF1F6] inline-flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" />
                    {hasDoc ? 'Replace' : 'Upload'}
                    <input type="file" hidden disabled={busy}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
                  </label>
                )}
              </div>
            </div>

            {data.done && (
              <div className="mt-4 flex items-end gap-4 flex-wrap">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-[#98A1B2] mb-1">Induction date</label>
                  <input type="date" value={when} disabled={!canEdit}
                    onChange={(e) => setWhen(e.target.value)} data-testid="induction-date"
                    className="border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[11px] uppercase tracking-wide text-[#98A1B2] mb-1">Remark (optional)</label>
                  <input type="text" value={remark} disabled={!canEdit}
                    onChange={(e) => setRemark(e.target.value)} data-testid="induction-remark"
                    className="w-full border border-[#E6E9F0] rounded-lg px-2 py-1.5 text-sm" />
                </div>
                {canEdit && (
                  <Button size="sm" onClick={() => save(true)} disabled={busy} data-testid="induction-save">
                    {busy ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </div>
            )}

            {data.done && data.on_joining_date === false && (
              <p className="mt-3 text-[12px] text-[#B14A0E] bg-[#FFF3E0] border border-[#FBD9A8] rounded p-2">
                Recorded on {data.induction_date}, not the joining date ({data.doj}). The printed
                criteria reads “if done same day of joining” — still 4 marks today, but the gap is
                on record.
              </p>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex items-start gap-2 text-[11px] text-[#98A1B2]">
        <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          {data.scoring_rule}
          {data.recorded_by && <> · last set by {data.recorded_by}
            {data.recorded_at && ` on ${new Date(data.recorded_at).toLocaleDateString()}`}</>}
        </span>
      </div>
    </div>
  );
}
